// accountRecords.js -- the account side of the consent ledger (spec §4). Single job: read the latest
// known consent per kind (all onboarding kinds at once, or one kind), build rows, and make ONE insert attempt classified terminal, transient or
// blocked. It does not report: its callers are the reporting boundary (CC-IMPL-010 satisfied there):
// consentOutbox.js for onboarding rows, featureConsent.js for feature consents.
import { APP_VERSION } from '../../lib/constants';

export const KNOWN = Object.freeze({ age: ['passed', 'under_21'], terms: ['accepted'], privacy: ['accepted'], slipsync: ['accepted'], bookies: ['accepted'] });
// Feature consents (#1415, #1421): one accepted/clickwrap row per agreement to a feature's disclosure.
export const FEATURE_KINDS = Object.freeze(['slipsync', 'bookies']);
export const TERMINAL_CODES = Object.freeze(['23514', '42501', '22P02', '23502']);
// The server's 24h under-21 guard (user_consents_age_block_guard).
export const BLOCKED_CODE = 'ST021';
const isKnown = (r) => r && Object.prototype.hasOwnProperty.call(KNOWN, r.kind) && KNOWN[r.kind].includes(r.result) && typeof r.version === 'string';

const COLUMNS = 'id,kind,version,result,attested_at,created_at';
// The newest row of one kind with one of `results`. Granted columns only: select=* would 42501. Ties on
// created_at resolve by id so the result is deterministic.
const newest = (client, userId, kind, results) => client.from('user_consents')
  .select(COLUMNS)
  .eq('user_id', userId)
  .eq('kind', kind)
  .in('result', results)
  .order('created_at', { ascending: false })
  .order('id', { ascending: false })
  .limit(1);

// Returns { age, terms, privacy, under21 }: the latest known row per kind, plus the newest age under_21
// row (spec §9 rule 4 keys the block on it, not on the latest age row). Four independent reads (#1438
// F5): one read capped across kinds let newer legal rows push an active under_21 row out of the page.
// The newer of two validated under_21 rows, by created_at (millisecond precision) then id, as the reads
// order them. The four reads are independent requests, each with its own snapshot: the general age read
// can see an under_21 committed after the dedicated read ran (#1461 P1-C). Either one is block evidence.
const newerU21 = (a, b) => {
  if (!a) return b;
  if (!b) return a;
  const ta = Date.parse(a.created_at);
  const tb = Date.parse(b.created_at);
  // An unparseable created_at never displaces a valid row, in either order (pre-Codex FC F2 / Sec F3).
  if (Number.isNaN(ta)) return b;
  if (Number.isNaN(tb)) return a;
  if (ta !== tb) return ta > tb ? a : b;
  return (a.id ?? 0) >= (b.id ?? 0) ? a : b;
};

export async function fetchLatestConsents(client, userId) {
  const answers = [['age', KNOWN.age], ['terms', KNOWN.terms], ['privacy', KNOWN.privacy], ['age', ['under_21']]];
  const reads = await Promise.all(answers.map(([kind, results]) => newest(client, userId, kind, results)));
  // A row that does not answer its read (wrong kind or result) is ignored, never trusted.
  const [age, terms, privacy, under21] = reads.map((res, i) => {
    if (res.error) return null;
    const r = (res.data || [])[0];
    const [kind, results] = answers[i];
    return isKnown(r) && r.kind === kind && results.includes(r.result) ? r : null;
  });
  // The newest under_21 either age read returned: the dedicated read, or the general one when its newest
  // age row is an under_21 (#1461 P1-C; pre-Codex F1 of #1458 when the dedicated read fails).
  const seenU21 = newerU21(under21, age && age.result === 'under_21' ? age : null);
  const failed = reads.find((r) => r.error);
  if (failed) {
    // #1458 P1-B: legal results from an incomplete read are never used, but a successfully read under_21
    // is block evidence: it rides on the error and the caller applies it.
    const err = new Error(`user_consents read failed: ${failed.error.message || 'unknown'}`);
    err.under21 = seenU21;
    throw err;
  }
  return { age, terms, privacy, under21: seenU21 };
}

export function buildRows({ userId, age, legal }) {
  const rows = [];
  if (age) rows.push({ user_id: userId, kind: 'age', version: age.version, result: age.result, method: 'dob_self_attest', attested_at: age.attestedAt, app_version: APP_VERSION });
  if (legal) {
    rows.push({ user_id: userId, kind: 'terms', version: legal.terms, result: 'accepted', method: 'clickwrap', attested_at: legal.attestedAt, app_version: APP_VERSION });
    rows.push({ user_id: userId, kind: 'privacy', version: legal.privacy, result: 'accepted', method: 'clickwrap', attested_at: legal.attestedAt, app_version: APP_VERSION });
  }
  return rows;
}

// The newest known row of one kind, or null (#1415 D3). A separate read so the gate's four-read
// fetchLatestConsents is unchanged. Throws on a read error: callers fail closed.
export async function fetchLatestConsent(client, userId, kind) {
  if (!Object.prototype.hasOwnProperty.call(KNOWN, kind)) throw new Error(`unknown consent kind: ${kind}`);
  const { data, error } = await newest(client, userId, kind, KNOWN[kind]);
  if (error) throw new Error(`user_consents read failed: ${error.message || 'unknown'}`);
  const r = (data || [])[0];
  return isKnown(r) && r.kind === kind ? r : null;
}

export function buildConsentRow({ userId, kind, version, attestedAt }) {
  if (!FEATURE_KINDS.includes(kind)) throw new Error(`not a feature consent kind: ${kind}`);
  return { user_id: userId, kind, version, result: 'accepted', method: 'clickwrap', attested_at: attestedAt, app_version: APP_VERSION };
}

// Plain insert (return=minimal): .select() after it would ask for return=representation and 42501.
// The caller's access token is pinned on the request (#1438 F2): supabase-js otherwise reads the token
// again at fetch time, so a sign-out or account switch after the caller's session check would send the
// rows as anon or as another user, and a 42501 would park valid rows. supabase-js keeps an existing
// Authorization header. No token: nothing is sent (transient; the outbox retries an onboarding row, and a
// feature consent goes back to the user to tap again).
export async function insertRows(client, rows, { accessToken } = {}) {
  if (!accessToken) return { ok: false, terminal: false, code: null };
  try {
    const { error } = await client.from('user_consents').insert(rows).setHeader('Authorization', `Bearer ${accessToken}`);
    if (!error) return { ok: true };
    const code = error.code || null;
    if (code === BLOCKED_CODE) return { ok: false, blocked: true, code };
    return { ok: false, terminal: TERMINAL_CODES.includes(code), code };
  } catch {
    return { ok: false, terminal: false, code: null };
  }
}
